// ── Design decisions ──────────────────────────────────────────────────────────
// Contenedor de la Encomienda de viaje (Viajero V2): un manifiesto = un viaje =
// muchos paquetes de muchos clientes.
//
// · Único dueño del estado transversal (viaje, clientes, config de impuestos) y
//   del autoguardado. Las tres pestañas son controladas: reciben datos + acciones.
// · Autoguardado SILENCIOSO a los 3 s con el patrón dirty-ref del contenedor V1
//   (src/viajero/ViajeroEncomiendaWorkspace.jsx): las ediciones de campo marcan
//   "sucio" y un solo timer manda el estado completo a autosaveViaje(); las
//   operaciones estructurales (crear/quitar bulto, paquete, artículo, confirmar
//   extracción) pegan a la API en el acto, como en V1 con package_id NOT NULL.
// · Estado de guardado: inactivo | guardando | guardado | fallo — el mismo
//   vocabulario que ProduccionPanel ya sabe mostrar.
// · Gate de arranque: sin viajeId se muestra el selector (viajes en borrador /
//   cerrados + "Nuevo viaje"). El montaje en App.js llega en otro brief, por eso
//   el export es temporal y autocontenido.
// · Cerrar / Reabrir son las únicas acciones terminales: viven en modales; el
//   resto en drawers o inline. Cerrado ⇒ Nueva Entrada y Manifiesto en solo
//   lectura y Producción se vuelve la pestaña activa.
// · Todo contra src/api/supabase/viajeroEncomienda.js. Cuando llegue la migración
//   VIAJERO-SCHEMA-V2 se cambia el import por un cliente real con las mismas
//   firmas; los componentes nunca tocan supabase.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, ClipboardList, Loader2, Plus, PackageOpen, Plane, ReceiptText, ShieldCheck } from 'lucide-react';
import * as api from '../../api/supabase/viajeroEncomienda';
import NuevaEntradaTab from './NuevaEntradaTab';
import ManifiestoTab from './ManifiestoTab';
import ProduccionTab from './ProduccionTab';
import PerfilAduaneroTab from './PerfilAduaneroTab';
import { EstadoChip } from './shared/chips';

const PULSO_AUTOGUARDADO = 3000;

const TABS = [
  { id: 'entrada', label: 'Nueva Entrada', Icon: PackageOpen },
  { id: 'manifiesto', label: 'Manifiesto', Icon: ClipboardList },
  { id: 'produccion', label: 'Producción', Icon: ReceiptText },
  { id: 'perfil', label: 'Perfil aduanero', Icon: ShieldCheck },
];

const fmtFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-SV', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Selector de viaje ────────────────────────────────────────────────────────
const SelectorViaje = ({ onAbrir, onBack }) => {
  const [estado, setEstado] = useState('cargando'); // cargando | ok | error
  const [viajes, setViajes] = useState([]);
  const [referencia, setReferencia] = useState('');
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState('');

  const cargar = useCallback(async () => {
    setEstado('cargando');
    try {
      const lista = await api.listViajes();
      setViajes(lista);
      setEstado('ok');
    } catch (err) {
      console.error('[ViajeroEncomienda] error listando viajes', err);
      setEstado('error');
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crear = async () => {
    setCreando(true);
    setErrorCrear('');
    try {
      const v = await api.createViaje({ trip_reference: referencia.trim() || null });
      onAbrir(v.id);
    } catch (err) {
      setErrorCrear(err?.message || 'No se pudo crear el viaje.');
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8">
      <div className="mx-auto max-w-3xl">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-4 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={13} aria-hidden="true" /> Volver
          </button>
        )}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Plane size={18} className="text-[var(--brand-teal)]" aria-hidden="true" />
            <h2 className="text-base font-bold text-slate-900">Encomienda de viaje</h2>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Un manifiesto por viaje. Retomá un borrador o abrí uno nuevo.
          </p>

          <form
            className="mt-5 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              crear();
            }}
          >
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Referencia del viaje, ej. Viaje Oct 15"
              aria-label="Referencia del viaje nuevo"
              className="min-h-[44px] flex-1 rounded-lg border border-slate-200 bg-[#f8fafc] px-3 text-sm text-slate-900 outline-none focus:border-[var(--brand-teal)]"
            />
            <button
              type="submit"
              disabled={creando}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              {creando ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
              {creando ? 'Creando…' : 'Nuevo viaje'}
            </button>
          </form>
          {errorCrear && (
            <p role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {errorCrear}
            </p>
          )}

          <div className="mt-6">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Viajes</h3>
            {estado === 'cargando' && (
              <p role="status" className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={13} className="animate-spin" aria-hidden="true" /> Cargando viajes…
              </p>
            )}
            {estado === 'error' && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                No se pudieron cargar los viajes.{' '}
                <button type="button" onClick={cargar} className="font-semibold underline">
                  Reintentar
                </button>
              </div>
            )}
            {estado === 'ok' && viajes.length === 0 && (
              <p className="mt-3 text-xs text-slate-400">
                Todavía no hay viajes. Abrí el primero con la referencia de arriba.
              </p>
            )}
            {estado === 'ok' && viajes.length > 0 && (
              <ul className="mt-2 divide-y divide-slate-100">
                {viajes.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => onAbrir(v.id)}
                      aria-label={`Abrir viaje ${v.trip_reference || 'sin referencia'}`}
                      className="flex min-h-[44px] w-full items-center justify-between gap-3 px-1 py-2 text-left hover:bg-slate-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">
                          {v.trip_reference || 'Viaje sin referencia'}
                        </span>
                        <span className="block text-[11px] text-slate-400">
                          {v.total_paquetes} paquetes · {v.total_bultos} bultos · {fmtFecha(v.updated_at)}
                        </span>
                      </span>
                      <EstadoChip status={v.status} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Contenedor ───────────────────────────────────────────────────────────────
const ViajeroEncomienda = ({ viajeId: viajeIdProp = null, onBack = null }) => {
  const [viajeId, setViajeId] = useState(viajeIdProp);
  const [gate, setGate] = useState(viajeIdProp ? 'loading' : 'selector'); // loading | selector | ok | error
  const [viaje, setViaje] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [taxConfig, setTaxConfig] = useState(null);
  const [tab, setTab] = useState('entrada');
  const [estadoGuardado, setEstadoGuardado] = useState('inactivo');
  const [ultimoAgregadoId, setUltimoAgregadoId] = useState(null);
  const [ultimoPaqueteId, setUltimoPaqueteId] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [intento, setIntento] = useState(0); // reintentos de carga

  const viajeRef = useRef(null);
  viajeRef.current = viaje;
  const sucioRef = useRef(false);
  const timerRef = useRef(null);

  // ── Carga ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!viajeId) return undefined;
    let cancelado = false;
    setGate('loading');
    (async () => {
      try {
        const [v, cs, cfg] = await Promise.all([
          api.getViaje(viajeId),
          api.listCustomers(),
          api.getTaxConfig(),
        ]);
        if (cancelado) return;
        setViaje(v);
        setCustomers(cs);
        setTaxConfig(cfg);
        setTab(v.status === 'cerrado' ? 'produccion' : 'entrada');
        setGate('ok');
      } catch (err) {
        console.error('[ViajeroEncomienda] error cargando el viaje', err);
        if (!cancelado) setGate('error');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [viajeId, intento]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  // ── Autoguardado ───────────────────────────────────────────────────────────
  const programarGuardado = useCallback(() => {
    sucioRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (!sucioRef.current) return;
      const actual = viajeRef.current;
      if (!actual?.id) return;
      setEstadoGuardado('guardando');
      try {
        sucioRef.current = false;
        await api.autosaveViaje(actual.id, {
          trip_reference: actual.trip_reference,
          bultos: actual.bultos,
          paquetes: actual.paquetes,
        });
        setEstadoGuardado('guardado');
        setTimeout(() => setEstadoGuardado((e) => (e === 'guardado' ? 'inactivo' : e)), 2500);
      } catch (err) {
        console.error('[ViajeroEncomienda] falló el autoguardado', err);
        sucioRef.current = true;
        setEstadoGuardado('fallo');
      }
    }, PULSO_AUTOGUARDADO);
  }, []);

  // Mutación local + marca sucia. `fn` recibe el viaje y devuelve el nuevo.
  const mutarLocal = useCallback(
    (fn) => {
      setViaje((prev) => (prev ? fn(prev) : prev));
      programarGuardado();
    },
    [programarGuardado]
  );

  const reportarFallo = (err, mensaje) => {
    console.error('[ViajeroEncomienda]', mensaje, err);
    setEstadoGuardado('fallo');
    toast.error(err?.message || mensaje);
  };

  // Si hay cambios pendientes, se vacían antes de una acción terminal.
  const vaciarPendientes = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const actual = viajeRef.current;
    if (!sucioRef.current || !actual?.id) return;
    sucioRef.current = false;
    await api.autosaveViaje(actual.id, {
      trip_reference: actual.trip_reference,
      bultos: actual.bultos,
      paquetes: actual.paquetes,
    });
  }, []);

  // Reemplazo del estado por una foto del servidor. TODA acción que termina en
  // "setViaje(respuesta del server)" pasa por acá: primero se vacía el
  // autoguardado pendiente (si no, la edición en vuelo se descarta y el timer
  // de 3 s la re-serializa vieja encima de la foto), después se trae la foto y
  // se desarma el timer — lo vaciado ya es la verdad del servidor, no queda
  // nada sucio que guardar.
  const reemplazarViaje = useCallback(
    async (fetchFn) => {
      await vaciarPendientes();
      const nuevo = await fetchFn();
      setViaje(nuevo);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      sucioRef.current = false;
      return nuevo;
    },
    [vaciarPendientes]
  );

  // ── Acciones ───────────────────────────────────────────────────────────────
  const setTripReference = useCallback(
    (trip_reference) => mutarLocal((v) => ({ ...v, trip_reference })),
    [mutarLocal]
  );

  const agregarBulto = useCallback(async () => {
    const v = viajeRef.current;
    if (!v) return;
    try {
      const b = await api.addBulto(v.id, {});
      setViaje((prev) => ({ ...prev, bultos: [...(prev.bultos || []), b] }));
    } catch (err) {
      reportarFallo(err, 'No se pudo crear el bulto.');
    }
  }, []);

  const cambiarBulto = useCallback(
    (bultoId, patch) =>
      mutarLocal((v) => ({
        ...v,
        bultos: (v.bultos || []).map((b) => (b.id === bultoId ? { ...b, ...patch } : b)),
      })),
    [mutarLocal]
  );

  const quitarBulto = useCallback(
    async (bultoId) => {
      const v = viajeRef.current;
      if (!v) return;
      try {
        await reemplazarViaje(() => api.removeBulto(v.id, bultoId));
      } catch (err) {
        reportarFallo(err, 'No se pudo quitar el bulto.');
      }
    },
    [reemplazarViaje]
  );

  const agregarPaquete = useCallback(async () => {
    const v = viajeRef.current;
    if (!v) return;
    try {
      const p = await api.addPaquete(v.id, {});
      setViaje((prev) => ({ ...prev, paquetes: [...(prev.paquetes || []), p] }));
      setUltimoPaqueteId(p.id);
    } catch (err) {
      reportarFallo(err, 'No se pudo crear el paquete.');
    }
  }, []);

  const cambiarPaquete = useCallback(
    (paqueteId, patch) =>
      mutarLocal((v) => ({
        ...v,
        paquetes: (v.paquetes || []).map((p) => (p.id === paqueteId ? { ...p, ...patch } : p)),
      })),
    [mutarLocal]
  );

  const cambiarNombrePaquete = useCallback(
    (paqueteId, nombre) => cambiarPaquete(paqueteId, { nombre }),
    [cambiarPaquete]
  );

  const quitarPaquete = useCallback(
    async (paqueteId) => {
      const v = viajeRef.current;
      if (!v) return;
      try {
        await reemplazarViaje(() => api.removePaquete(v.id, paqueteId));
      } catch (err) {
        reportarFallo(err, 'No se pudo quitar el paquete.');
      }
    },
    [reemplazarViaje]
  );

  const agregarItem = useCallback(async (paqueteId) => {
    const v = viajeRef.current;
    if (!v) return;
    try {
      const it = await api.addItem(v.id, paqueteId, {});
      setViaje((prev) => ({
        ...prev,
        paquetes: (prev.paquetes || []).map((p) =>
          p.id === paqueteId ? { ...p, items: [...(p.items || []), it] } : p
        ),
      }));
      setUltimoAgregadoId(it.id);
    } catch (err) {
      reportarFallo(err, 'No se pudo agregar el artículo.');
    }
  }, []);

  const cambiarItem = useCallback(
    (itemId, patch) =>
      mutarLocal((v) => ({
        ...v,
        paquetes: (v.paquetes || []).map((p) => ({
          ...p,
          items: (p.items || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
        })),
      })),
    [mutarLocal]
  );

  const quitarItem = useCallback(
    async (itemId) => {
      const v = viajeRef.current;
      if (!v) return;
      try {
        await reemplazarViaje(() => api.removeItem(v.id, itemId));
      } catch (err) {
        reportarFallo(err, 'No se pudo eliminar el artículo.');
      }
    },
    [reemplazarViaje]
  );

  const quickCreateCustomer = useCallback(async (datos) => {
    const c = await api.quickCreateCustomer(datos);
    setCustomers((prev) => [...prev, c]);
    toast.success(`Cliente ${c.full_name} creado (${c.id})`);
    return c;
  }, []);

  const confirmarExtraccion = useCallback(
    async (groups) => {
      const v = viajeRef.current;
      if (!v) return;
      await reemplazarViaje(() => api.confirmExtraction(v.id, groups));
      const n = groups.reduce((s, g) => s + (g.items || []).length, 0);
      toast.success(`${groups.length} paquetes y ${n} artículos agregados al viaje`);
    },
    [reemplazarViaje]
  );

  const cerrarManifiesto = useCallback(async () => {
    const v = viajeRef.current;
    if (!v) return;
    setCerrando(true);
    try {
      await reemplazarViaje(() => api.cerrarManifiesto(v.id));
      setTab('produccion');
      toast.success('Manifiesto cerrado');
    } catch (err) {
      toast.error(err?.message || 'No se pudo cerrar el manifiesto.');
      throw err;
    } finally {
      setCerrando(false);
    }
  }, [reemplazarViaje]);

  const reabrirManifiesto = useCallback(async () => {
    const v = viajeRef.current;
    if (!v) return;
    try {
      await reemplazarViaje(() => api.reabrirManifiesto(v.id));
      setTab('manifiesto');
      toast.success('Manifiesto reabierto');
    } catch (err) {
      toast.error(err?.message || 'No se pudo reabrir el manifiesto.');
      throw err;
    }
  }, [reemplazarViaje]);

  const exportXlsx = useCallback(async () => {
    const v = viajeRef.current;
    const r = await api.exportXlsx(v.id);
    toast.success(`XLSX listo: ${r.filename}`);
    return r;
  }, []);

  const generarPdf = useCallback(async () => {
    const v = viajeRef.current;
    const r = await api.generarAnexoPdf(v.id);
    if (!r.ok) {
      const msg = r.incompletos?.length
        ? `${r.error} Revisá: ${r.incompletos.join(', ')}`
        : r.error;
      toast.error(msg);
      // Se relanza: AccionBoton pinta su error inline al rechazar — devolver
      // acá mostraría un "Listo." bajo una generación fallida.
      throw new Error(msg);
    }
    // La descarga vive en el componente: el adaptador entrega bytes puros sin
    // DOM. Bytes vacíos (mock en jsdom, que no implementa createObjectURL) no
    // disparan descarga.
    if (r.bytes?.length) {
      const blob = new Blob([r.bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
    }
    toast.success(`Anexo PDF listo: ${r.filename}`);
    return r;
  }, []);

  const acciones = {
    setTripReference,
    agregarBulto,
    cambiarBulto,
    quitarBulto,
    agregarPaquete,
    cambiarPaquete,
    cambiarNombrePaquete,
    quitarPaquete,
    agregarItem,
    cambiarItem,
    quitarItem,
    quickCreateCustomer,
    confirmarExtraccion,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (gate === 'selector') {
    return (
      <>
        <Toaster position="bottom-right" />
        <SelectorViaje onAbrir={(id) => setViajeId(id)} onBack={onBack} />
      </>
    );
  }

  if (gate === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8fafc]">
        <p role="status" className="inline-flex items-center gap-2 text-sm font-bold text-slate-500">
          <Loader2 size={15} className="animate-spin" aria-hidden="true" /> Cargando viaje…
        </p>
      </div>
    );
  }

  if (gate === 'error') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-8">
        <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <p className="text-sm font-bold text-slate-900">No se pudo abrir el viaje</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">
            Hubo un problema al leer los datos. Volvé a intentar en unos segundos.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => setIntento((n) => n + 1)}
              className="inline-flex min-h-[44px] items-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white"
            >
              Reintentar
            </button>
            <button
              type="button"
              onClick={() => {
                setViajeId(null);
                setGate('selector');
              }}
              className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft size={13} aria-hidden="true" /> Volver a los viajes
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cerrado = viaje?.status === 'cerrado';

  return (
    <div className="flex h-screen min-h-0 flex-col bg-[#f8fafc]">
      <Toaster position="bottom-right" />
      <header className="border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 px-4 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (onBack && viajeIdProp) onBack();
                else {
                  setViajeId(null);
                  setViaje(null);
                  setGate('selector');
                }
              }}
              aria-label="Volver a los viajes"
              className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft size={13} aria-hidden="true" /> Viajes
            </button>
            <Plane size={15} className="shrink-0 text-[var(--brand-teal)]" aria-hidden="true" />
            <span className="truncate text-sm font-bold text-slate-900">
              {viaje?.trip_reference || 'Viaje sin referencia'}
            </span>
            <EstadoChip status={viaje?.status} />
          </div>
          <span
            role="status"
            aria-live="polite"
            className={`text-[11px] ${estadoGuardado === 'fallo' ? 'text-red-600' : 'text-slate-400'}`}
          >
            {estadoGuardado === 'guardando' && 'Guardando…'}
            {estadoGuardado === 'guardado' && 'Guardado'}
            {estadoGuardado === 'fallo' && 'No se pudo guardar'}
          </span>
        </div>
        <nav role="tablist" aria-label="Secciones del viaje" className="flex gap-1 px-2">
          {TABS.map(({ id, label, Icon }) => {
            const activo = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activo}
                aria-controls={`panel-${id}`}
                onClick={() => setTab(id)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 border-b-2 px-3 text-sm font-semibold transition-colors ${
                  activo
                    ? 'border-[var(--brand-teal)] text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-900'
                }`}
              >
                <Icon size={15} aria-hidden="true" /> {label}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'entrada' && (
          <div id="panel-entrada" role="tabpanel" className="h-full min-h-0">
            <NuevaEntradaTab
              viaje={viaje}
              customers={customers}
              estadoGuardado={estadoGuardado}
              ultimoAgregadoId={ultimoAgregadoId}
              ultimoPaqueteId={ultimoPaqueteId}
              readOnly={cerrado}
              acciones={acciones}
              api={{
                searchCatalog: api.searchCatalog,
                extractLista: api.extractLista,
                obtenerBorradorExtraccion: api.obtenerBorradorExtraccion,
                guardarBorradorExtraccion: api.guardarBorradorExtraccion,
                eliminarBorradorExtraccion: api.eliminarBorradorExtraccion,
              }}
            />
          </div>
        )}
        {tab === 'manifiesto' && (
          <div id="panel-manifiesto" role="tabpanel" className="h-full min-h-0">
            <ManifiestoTab
              viaje={viaje}
              customers={customers}
              acciones={acciones}
              onCerrar={cerrarManifiesto}
              cerrando={cerrando}
              readOnly={cerrado}
              api={{ searchCatalog: api.searchCatalog }}
            />
          </div>
        )}
        {tab === 'produccion' && (
          <div id="panel-produccion" role="tabpanel" className="h-full min-h-0">
            <ProduccionTab
              viaje={viaje}
              customers={customers}
              taxConfig={taxConfig}
              onReabrir={reabrirManifiesto}
              onExportXlsx={exportXlsx}
              onGenerarPdf={generarPdf}
            />
          </div>
        )}
        {tab === 'perfil' && (
          <div id="panel-perfil" role="tabpanel" className="h-full min-h-0">
            <PerfilAduaneroTab />
          </div>
        )}
      </div>
    </div>
  );
};

export default ViajeroEncomienda;
